import React from 'react';
import PropTypes from 'prop-types';
import { __ } from '@wordpress/i18n';
import { formatCurrency, getCurrencySymbol } from '@src/scripts/util/currency';

// Mirrors the reference tool's "Monatliche Leasingfinanzierung" step: the
// term and interest rate derive a calculated rate, and the user can enter
// their actual contractual rate if a real financing offer differs from it.
const LeasingStep = ( {
	leaseTermMonths,
	annualInterestRate,
	calculatedMonthlyRate,
	contractualRateOverride,
	onChangeTerm,
	onChangeInterestRate,
	onChangeContractualRate,
	currency,
	locale,
} ) => (
	<div className="roi-calculator__step roi-calculator__step--leasing">
		<h3>{ __( 'Monthly lease financing', 'sprintray-roi-calculator' ) }</h3>
		<p className="roi-calculator__step-hint">
			{ __(
				'Does the device earn more than it costs per month?',
				'sprintray-roi-calculator'
			) }
		</p>
		<div className="roi-calculator__leasing-fields">
			<label
				className="roi-calculator__field"
				htmlFor="roi-calculator-lease-term"
			>
				{ __( 'Term (months)', 'sprintray-roi-calculator' ) }
				<input
					id="roi-calculator-lease-term"
					type="number"
					min="1"
					step="1"
					// Clearing the field used to store 0 (Number('') === 0),
					// which redisplayed as "0" instead of blank — typing a
					// new digit then landed after that "0" instead of
					// replacing it. Reporting `null` on empty (rather than
					// 0) and rendering it as '' lets the field go blank.
					value={ leaseTermMonths ?? '' }
					onChange={ ( event ) => {
						const raw = event.target.value;
						onChangeTerm( raw === '' ? null : Number( raw ) );
					} }
				/>
			</label>
			<label
				className="roi-calculator__field"
				htmlFor="roi-calculator-lease-interest"
			>
				{ __( 'Annual interest rate (%)', 'sprintray-roi-calculator' ) }
				<input
					id="roi-calculator-lease-interest"
					type="number"
					min="0"
					step="1"
					value={ annualInterestRate ?? '' }
					onChange={ ( event ) => {
						const raw = event.target.value;
						onChangeInterestRate(
							raw === '' ? null : Number( raw )
						);
					} }
				/>
			</label>
			<div className="roi-calculator__field">
				<span id="roi-calculator-lease-calculated-rate-label">
					{ __( 'Calculated rate', 'sprintray-roi-calculator' ) }
				</span>
				<span
					className="roi-calculator__field-readout"
					aria-labelledby="roi-calculator-lease-calculated-rate-label"
				>
					{ formatCurrency(
						calculatedMonthlyRate,
						currency,
						locale
					) }
					/{ __( 'month', 'sprintray-roi-calculator' ) }
				</span>
			</div>
			<label
				className="roi-calculator__field"
				htmlFor="roi-calculator-lease-contractual-rate"
			>
				{ __( 'Contractual rate', 'sprintray-roi-calculator' ) }
				<span className="roi-calculator__input-suffix">
					<input
						id="roi-calculator-lease-contractual-rate"
						type="number"
						min="0"
						step="1"
						placeholder={ String(
							Math.round( calculatedMonthlyRate )
						) }
						value={ contractualRateOverride ?? '' }
						onChange={ ( event ) =>
							onChangeContractualRate(
								event.target.value === ''
									? null
									: Number( event.target.value )
							)
						}
					/>
					<span aria-hidden="true">
						{ getCurrencySymbol( currency, locale ) }
					</span>
				</span>
				<span className="roi-calculator__field-hint">
					{ __(
						'Leave blank to use the calculated rate.',
						'sprintray-roi-calculator'
					) }
				</span>
			</label>
		</div>
	</div>
);

LeasingStep.propTypes = {
	// Not .isRequired: both go briefly null while the user has cleared the
	// field to type a new value (see the input's own onChange above).
	leaseTermMonths: PropTypes.number,
	annualInterestRate: PropTypes.number,
	calculatedMonthlyRate: PropTypes.number.isRequired,
	contractualRateOverride: PropTypes.number,
	onChangeTerm: PropTypes.func.isRequired,
	onChangeInterestRate: PropTypes.func.isRequired,
	onChangeContractualRate: PropTypes.func.isRequired,
	currency: PropTypes.string,
	locale: PropTypes.string,
};

export default LeasingStep;
